import React from 'react';
import styled from 'styled-components';
import useApi from "../hooks/api/axiosInterceptor";

// 스타일 컴포넌트 정의
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
`;


const Nav = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  color: #666;

  li {
    display: flex;
    align-items: center;
    
    &:not(:last-child)::after {
      content: '>';
      margin: 0 8px;
    }
  }
`;

const BoardView = styled.div`
  background-color: #fff;
  border-top: 2px solid #333;
  margin-bottom: 30px;
`;

const BoardViewTop = styled.div`
  padding: 20px;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
`;

const Info = styled.div`
  display: flex;
  font-size: 14px;
  color: #666;

  span {
    margin-right: 20px;
  }
`;

const BoardViewCont = styled.div`
  padding: 30px 20px;
  min-height: 300px;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const FileArea = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  h4 {
    font-size: 16px;
    margin-bottom: 10px;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    margin-bottom: 5px;
  }

  a {
    color: #0066cc;
    text-decoration: none;
    display: flex;
    align-items: center;

    &:hover {
      text-decoration: underline;
    }

    &::before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      background-image: url('/assets/images/icon_file.gif');
      background-size: contain;
      margin-right: 5px;
    }
  }
`;

const BtnWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
  gap: 10px;

  a {
    display: inline-block;
    padding: 8px 20px;
    border-radius: 4px;
    text-decoration: none;
    font-size: 14px;
    transition: background-color 0.3s;
  }
`;

const BtnList = styled.a`
  background-color: #666;
  color: white;
  
  &:hover {
    background-color: #555;
  }
`;

const BtnModify = styled.a`
  background-color: #4b70fd;
  color: white;
  
  &:hover {
    background-color: #3a5fec;
  }
`;

const BtnDelete = styled.a`
  background-color: #ff6b6b;
  color: white;
  
  &:hover {
    background-color: #ff5252;
  }
`;

const PrevNext = styled.div`
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
`;

const PrevNextItem = styled.div`
  display: flex;
  padding: 15px 20px;
  
  &:first-of-type {
    border-bottom: 1px solid #eee;
  }
  
  span {
    flex: 0 0 80px;
    font-weight: 600;
  }
  
  a {
    color: #333;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const handleFileDownload = async (e, fileName, postId) => {
  e.preventDefault();
  console.log('Downloading file:', fileName);

  try {
    // 파일명을 Base64로 인코딩 - 인코딩 문제를 완전히 피하기 위함
    const base64FileName = btoa(unescape(encodeURIComponent(fileName)));

    const response = await useApi.post(
        '/files/download',
        {
          fileName: base64FileName, // Base64 인코딩된 파일명 전송
          originalName: fileName,    // 디버깅용 원본 파일명도 함께 전송
          postId
        },
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          responseType: 'blob', // blob으로 받기
        }
    );

    // Content-Disposition 헤더에서 파일명 추출 시도
    const contentDisposition = response.headers['content-disposition'];
    let downloadFileName = fileName; // 기본값으로 원래 파일명 사용

    if (contentDisposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches[1]) {
        // 헤더에서 파일명 추출 성공 시 해당 파일명 사용
        downloadFileName = matches[1].replace(/['"]/g, '');
        try {
          // UTF-8로 디코딩 시도
          downloadFileName = decodeURIComponent(downloadFileName);
        } catch (e) {
          console.warn('Failed to decode filename from header:', e);
        }
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', downloadFileName);
    document.body.appendChild(a);
    a.click();

    // 정리
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);
  } catch (error) {
    console.error('Error downloading file:', error);
    alert('파일 다운로드에 실패했습니다.');
  }
};



// 게시판 상세 페이지 컴포넌트
const BoardList = ({ post, onBack, onModify, onDelete, prevPost, nextPost }) => {
  if (!post) return <div>로딩 중...</div>;

  return (
    <Container>
      <BoardView>
        <BoardViewTop>
          <Title>{post.title}</Title>
          <Info>
            <span className="writer">작성자: {post.author}</span>
            <span className="date">작성일: {post.createdAt}</span>
            <span className="views">조회수: {post.views}</span>
          </Info>
        </BoardViewTop>
        
        <BoardViewCont>
          {/* dangerouslySetInnerHTML은 XSS 위험이 있으므로 신뢰할 수 있는 컨텐츠에만 사용해야 합니다 */}
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
          
          {post.attachments && post.attachments.length > 0 && (
            <FileArea>
              <h4>첨부파일</h4>
              <ul>
                {post.attachments.map((file, index) => (
                  <li key={index}>
           { <a href="#" onClick={(e) => handleFileDownload(e, file, post.id)}>
            {file}
           </a> }
        </li>			
                ))}
              </ul>
            </FileArea>
          )}
        </BoardViewCont>
        
        <BtnWrap>
          <BtnList onClick={onBack}>목록</BtnList>
          {onModify && <BtnModify onClick={onModify}>수정</BtnModify>}
          {onDelete && <BtnDelete onClick={onDelete}>삭제</BtnDelete>}
        </BtnWrap>
        
        <PrevNext>
          {prevPost && (
            <PrevNextItem className="prev">
              <span>이전글</span>
              <a href={`/board/${prevPost.id}`}>{prevPost.title}</a>
            </PrevNextItem>
          )}
          {nextPost && (
            <PrevNextItem className="next">
              <span>다음글</span>
              <a href={`/board/${nextPost.id}`}>{nextPost.title}</a>
            </PrevNextItem>
          )}
        </PrevNext>
      </BoardView>
    </Container>
  );
};

export default BoardList;
